import { useState, useEffect } from 'react';
import {
  listRejectedRestaurants,
  type RestaurantApplication,
} from '@/shared/services/restaurantsService';
import { ImageCarousel } from '@/shared/components/ImageCarousel';
import styles from './RejectedRestaurantsPage.module.css';

export function RejectedRestaurantsPage() {
  const [applications, setApplications] = useState<RestaurantApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError('');
      const apps = await listRejectedRestaurants();
      setApplications(apps);
    } catch (err) {
      let errorMessage = 'Error al cargar solicitudes rechazadas';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string }; status?: number } };
        if (axiosError.response?.status === 404) {
          errorMessage = 'El endpoint no está disponible. Verifica que el backend esté desplegado.';
        } else if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
          errorMessage = 'No tienes permisos para ver solicitudes rechazadas.';
        } else {
          errorMessage = axiosError.response?.data?.message || axiosError.response?.data?.error || errorMessage;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp?: { seconds: number; nanoseconds: number }) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Cargando solicitudes rechazadas...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Solicitudes Rechazadas</h1>
        <p className={styles.subtitle}>Historial de solicitudes de restaurantes rechazadas por el administrador</p>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {applications.length === 0 ? (
        <div className={styles.empty}>
          <p>No hay solicitudes rechazadas en este momento.</p>
        </div>
      ) : (
        <div className={styles.applicationsList}>
          {applications.map((app) => (
            <div key={app.applicationId} className={styles.applicationCard}>
              <div className={styles.cardHeader}>
                <h3 className={styles.restaurantName}>{app.name}</h3>
                <span className={styles.statusBadge}>{app.status}</span>
              </div>

              <div className={styles.cardBody}>
                {app.images && app.images.length > 0 && (
                  <div className={styles.imagesSection}>
                    <span className={styles.label}>Imágenes del restaurante:</span>
                    <div className={styles.imagesContainer}>
                      <ImageCarousel images={app.images} maxImages={3} />
                    </div>
                  </div>
                )}
                <div className={styles.infoRow}>
                  <span className={styles.label}>Email:</span>
                  <span className={styles.value}>{app.email}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Teléfono:</span>
                  <span className={styles.value}>{app.phone}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Dirección:</span>
                  <span className={styles.value}>{app.addressText}</span>
                </div>
                {app.addressLat && app.addressLng && (
                  <div className={styles.infoRow}>
                    <span className={styles.label}>Coordenadas:</span>
                    <span className={styles.value}>
                      {app.addressLat.toFixed(6)}, {app.addressLng.toFixed(6)}
                    </span>
                  </div>
                )}
                {app.rejectionReason && (
                  <div className={styles.infoRow}>
                    <span className={styles.label}>Motivo del rechazo:</span>
                    <span className={styles.value}>{app.rejectionReason}</span>
                  </div>
                )}
                {app.reviewedAt && (
                  <div className={styles.infoRow}>
                    <span className={styles.label}>Fecha de rechazo:</span>
                    <span className={styles.value}>{formatDate(app.reviewedAt)}</span>
                  </div>
                )}
                <div className={styles.infoRow}>
                  <span className={styles.label}>Fecha de solicitud:</span>
                  <span className={styles.value}>{formatDate(app.createdAt)}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>ID de solicitud:</span>
                  <span className={styles.value}>{app.applicationId}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <p className={styles.count}>Total: {applications.length} solicitudes rechazadas</p>
        <button onClick={loadApplications} className={styles.refreshButton} disabled={loading}>
          {loading ? 'Actualizando...' : 'Actualizar Lista'}
        </button>
      </div>
    </div>
  );
}

